import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useStoreByName } from "./use-stores.hooks";
import * as adminService from "../services/api/admin.service";
import { runInAction } from "mobx";
import { useEffect } from "react";
import { ROLES } from "@shared/types";

/**
 * TanStack Query v5 hooks for admin endpoints
 * Following the work plan: services → TanStack Query → MobX store sync
 * 
 * Rules:
 * 1. Do not modify existing stores
 * 2. Do not modify existing services  
 * 3. Each hook uses clear queryKey
 * 4. Always update MobX store using useEffect (v5 pattern)
 * 5. Always invalidate queries after mutations
 */

// ==================== FETCH QUERIES ====================

/**
 * Fetch users list
 */
export function useAdminUsers(schoolId?: string) {
  const adminStore = useStoreByName("adminStore");
  
  const query = useQuery({
    queryKey: ["admin", "users", schoolId],
    queryFn: () => adminService.getUsers(schoolId),
  });

  // Sync with MobX store when data changes
  useEffect(() => {
    if (query.data) {
      runInAction(() => {
        adminStore.users = query.data.users || query.data;
        adminStore.setError(null);
      });
    }
    if (query.error) {
      runInAction(() => {
        adminStore.setError((query.error as Error).message || "Failed to load users");
      });
    }
  }, [query.data, query.error, adminStore]);

  return query;
}

/**
 * Fetch single user by ID
 */
export function useAdminUser(userId: string) {
  const adminStore = useStoreByName("adminStore");
  
  const query = useQuery({
    queryKey: ["admin", "user", userId],
    queryFn: () => adminService.getUserById(userId),
    enabled: !!userId,
  });

  useEffect(() => {
    if (query.data) {
      runInAction(() => {
        adminStore.currentUser = query.data;
        adminStore.setError(null);
      });
    }
    if (query.error) {
      runInAction(() => {
        adminStore.setError((query.error as Error).message || "Failed to load user");
      });
    }
  }, [query.data, query.error, adminStore]);

  return query;
}

/**
 * Fetch system overview statistics
 */
export function useAdminSystemOverview(schoolId?: string) {
  const adminStore = useStoreByName("adminStore");
  
  const query = useQuery({
    queryKey: ["admin", "system-overview", schoolId],
    queryFn: () => adminService.getSystemOverview(schoolId),
  });

  useEffect(() => {
    if (query.data) {
      runInAction(() => {
        adminStore.systemOverview = query.data;
        adminStore.setError(null);
      });
    }
    if (query.error) {
      runInAction(() => {
        adminStore.setError((query.error as Error).message || "Failed to load system overview");
      });
    }
  }, [query.data, query.error, adminStore]);

  return query;
}

/**
 * Fetch score statistics
 */
export function useAdminScoreStatistics(schoolId?: string) {
  const adminStore = useStoreByName("adminStore");
  
  const query = useQuery({
    queryKey: ["admin", "score-statistics", schoolId],
    queryFn: () => adminService.getScoreStatistics(schoolId),
  });

  useEffect(() => {
    if (query.data) {
      runInAction(() => {
        adminStore.scoreStatistics = query.data;
        adminStore.setError(null);
      });
    }
    if (query.error) {
      runInAction(() => {
        adminStore.setError((query.error as Error).message || "Failed to load score statistics");
      });
    }
  }, [query.data, query.error, adminStore]);

  return query;
}

/**
 * Fetch user growth data
 */
export function useAdminUserGrowthData(schoolId?: string) {
  const adminStore = useStoreByName("adminStore");
  
  const query = useQuery({
    queryKey: ["admin", "user-growth", schoolId],
    queryFn: () => adminService.getUserGrowthData(schoolId),
  });

  useEffect(() => {
    if (query.data) {
      runInAction(() => {
        adminStore.userGrowthData = query.data;
        adminStore.setError(null);
      });
    }
    if (query.error) {
      runInAction(() => {
        adminStore.setError((query.error as Error).message || "Failed to load user growth data");
      });
    }
  }, [query.data, query.error, adminStore]);

  return query;
}

/**
 * Fetch most active entities
 */
export function useAdminMostActiveEntities(schoolId?: string) {
  const adminStore = useStoreByName("adminStore");
  
  const query = useQuery({
    queryKey: ["admin", "most-active", schoolId],
    queryFn: () => adminService.getMostActiveEntities(schoolId),
  });

  useEffect(() => {
    if (query.data) {
      runInAction(() => {
        adminStore.mostActiveEntities = query.data;
        adminStore.setError(null);
      });
    }
    if (query.error) {
      runInAction(() => {
        adminStore.setError((query.error as Error).message || "Failed to load most active entities");
      });
    }
  }, [query.data, query.error, adminStore]);

  return query;
}

/**
 * Fetch activity metrics - comprehensive hierarchical data
 * Always fetches all schools data, filtering is done client-side
 */
export function useAdminActivityMetrics(daysAgo = 30) {
  const adminStore = useStoreByName("adminStore");
  
  const query = useQuery({
    queryKey: ["admin", "activity-metrics", daysAgo],
    queryFn: () => adminService.getActivityMetrics(undefined, daysAgo),
  });

  useEffect(() => {
    if (query.data) {
      runInAction(() => {
        adminStore.comprehensiveActivityData = query.data;
        adminStore.activityMetrics = query.data;
        adminStore.setError(null);
      });
    }
    if (query.error) {
      runInAction(() => {
        adminStore.setError((query.error as Error).message || "Failed to load activity metrics");
      });
    }
  }, [query.data, query.error, adminStore]);

  return query;
}

/**
 * Fetch classes data
 */
export function useAdminClasses(schoolId?: string) {
  const adminStore = useStoreByName("adminStore");
  
  const query = useQuery({
    queryKey: ["admin", "classes", schoolId],
    queryFn: () => adminService.getClasses(schoolId),
  });

  useEffect(() => {
    if (query.data) {
      runInAction(() => {
        adminStore.classes = query.data;
        adminStore.setError(null);
      });
    }
    if (query.error) {
      runInAction(() => {
        adminStore.setError((query.error as Error).message || "Failed to load classes");
      });
    }
  }, [query.data, query.error, adminStore]);

  return query;
}

/**
 * Fetch schools and grades
 */
export function useAdminSchoolsAndGrades() {
  const adminStore = useStoreByName("adminStore");
  const userStore = useStoreByName("userStore");
  
  const query = useQuery({
    queryKey: ["admin", "schools-and-grades"],
    queryFn: () => adminService.getSchoolsAndGrades(),
  });

  useEffect(() => {
    if (query.data) {
      runInAction(() => {
        adminStore.availableSchools = query.data.schools || [];
        adminStore.setError(null);

        const userRoleId = userStore.userData?.personalInfo?.role?.id;
        const isPrincipal = userRoleId === ROLES.PRINCIPAL;
        const schools = query.data.schools || [];

        if (isPrincipal && schools.length > 0 && !adminStore.selectedSchoolId) {
          adminStore.selectedSchoolId = String(schools[0].id);
        }
      });
    }
    if (query.error) {
      runInAction(() => {
        adminStore.setError((query.error as Error).message || "Failed to load schools and grades");
      });
    }
  }, [query.data, query.error, adminStore, userStore]);

  return query;
}

// ==================== MUTATIONS ====================
// Note: Mutations in TanStack Query v5 still support onSuccess/onError callbacks

/**
 * Create new user
 */
export function useCreateAdminUser() {
  const queryClient = useQueryClient();
  const adminStore = useStoreByName("adminStore");
  
  return useMutation({
    mutationFn: (userData: any) => adminService.createUser(userData),
    onSuccess: () => {
      // Update store
      runInAction(() => {
        adminStore.setError(null);
      });
      
      // Invalidate related queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "system-overview"] });
    },
    onError: (error: Error) => {
      runInAction(() => {
        adminStore.setError(error.message || "Failed to create user");
      });
    },
  });
}

/**
 * Update existing user
 */
export function useUpdateAdminUser() {
  const queryClient = useQueryClient();
  const adminStore = useStoreByName("adminStore");
  
  return useMutation({
    mutationFn: ({ userId, userData }: { userId: string; userData: any }) => 
      adminService.updateUser(userId, userData),
    onSuccess: (data, variables) => {
      // Update store
      runInAction(() => {
        if (adminStore.currentUser?.id === variables.userId) {
          adminStore.currentUser = data;
        }
        adminStore.setError(null);
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ["admin", "user", variables.userId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (error: Error) => {
      runInAction(() => {
        adminStore.setError(error.message || "Failed to update user");
      });
    },
  });
}

/**
 * Delete user
 */
export function useDeleteAdminUser() {
  const queryClient = useQueryClient();
  const adminStore = useStoreByName("adminStore");
  
  return useMutation({
    mutationFn: ({ userId, forceDelete = false }: { userId: string; forceDelete?: boolean }) => 
      adminService.deleteUser(userId, forceDelete),
    onSuccess: (_data, variables) => {
      // Update store
      runInAction(() => {
        // Remove user from local store
        adminStore.users = adminStore.users.filter((u: any) => u.id !== variables.userId);
        if (adminStore.currentUser?.id === variables.userId) {
          adminStore.currentUser = null;
        }
        adminStore.setError(null);
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "system-overview"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "activity-metrics"] });
    },
    onError: (error: Error) => {
      runInAction(() => {
        adminStore.setError(error.message || "Failed to delete user");
      });
    },
  });
}

/**
 * Create new school
 */
export function useCreateAdminSchool() {
  const queryClient = useQueryClient();
  const adminStore = useStoreByName("adminStore");
  
  return useMutation({
    mutationFn: (schoolData: { name: string }) => 
      adminService.createSchool(schoolData),
    onSuccess: (data) => {
      // Update store
      runInAction(() => {
        adminStore.availableSchools.push(data);
        adminStore.setError(null);
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ["admin", "schools-and-grades"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "system-overview"] });
    },
    onError: (error: Error) => {
      runInAction(() => {
        adminStore.setError(error.message || "Failed to create school");
      });
    },
  });
}

/**
 * Create new grade/class
 */
export function useCreateAdminGrade() {
  const queryClient = useQueryClient();
  const adminStore = useStoreByName("adminStore");
  
  return useMutation({
    mutationFn: (gradeData: { name: string; school_id: number; teacher_id: number }) => 
      adminService.createGrade(gradeData),
    onSuccess: () => {
      // Update store
      runInAction(() => {
        adminStore.setError(null);
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ["admin", "schools-and-grades"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
    },
    onError: (error: Error) => {
      runInAction(() => {
        adminStore.setError(error.message || "Failed to create grade");
      });
    },
  });
}

/**
 * Delete school
 */
export function useDeleteAdminSchool() {
  const queryClient = useQueryClient();
  const adminStore = useStoreByName("adminStore");
  
  return useMutation({
    mutationFn: (schoolId: number) => 
      adminService.deleteSchool(schoolId),
    onSuccess: (_data, schoolId) => {
      // Update store
      runInAction(() => {
        adminStore.availableSchools = adminStore.availableSchools.filter(
          (s: any) => s.id !== schoolId
        );
        adminStore.setError(null);
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ["admin", "schools-and-grades"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "system-overview"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (error: Error) => {
      runInAction(() => {
        adminStore.setError(error.message || "Failed to delete school");
      });
    },
  });
}

/**
 * Delete grade/class
 */
export function useDeleteAdminGrade() {
  const queryClient = useQueryClient();
  const adminStore = useStoreByName("adminStore");
  
  return useMutation({
    mutationFn: (gradeId: number) => 
      adminService.deleteGrade(gradeId),
    onSuccess: () => {
      // Update store
      runInAction(() => {
        adminStore.setError(null);
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ["admin", "schools-and-grades"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "classes"] });
    },
    onError: (error: Error) => {
      runInAction(() => {
        adminStore.setError(error.message || "Failed to delete grade");
      });
    },
  });
}